import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, Tag, Space, Button, Spin, Modal, Form, Input, Select, notification, Row, Col, Checkbox } from "antd";
import styled from "styled-components";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  getDeliveryMethodsForAdmin,
  createDeliveryMethodAdmin,
  updateDeliveryMethodAdmin,
  deleteDeliveryMethodAdmin,
  getDeliveryZones,
  updateDeliveryZone,
  deleteDeliveryZone,
} from "../../../service/client";
import { useBranchContext } from "../../../contexts/branch-context";
import type { ColumnsType } from "antd/es/table";
import { DeliveryMap } from "../delivery-map/DeliveryMap";
import { DeleteOutlined } from "@ant-design/icons";
import { StyledSwitch, SectionTitle, SettingItem, SettingRow, SettingContent, SettingLabel, SettingDescription } from "../../../styles/shared-components";
import { colors } from "../../../styles/theme";

interface DeliveryMethod {
  id: number;
  name: string;
  type: string;
  config?: any;
  isActive: boolean;
}

const deliveryTypeLabelKeys: Record<string, string> = {
  TO_THE_ADDRESS: "deliveryMethodsSettings.typeToTheAddress",
  SELF_PICKUP: "deliveryMethodsSettings.typeSelfPickup",
  TO_THE_TABLE: "deliveryMethodsSettings.typeToTheTable",
  EMAIL: "deliveryMethodsSettings.typeEmail",
};

const deliveryTypeColors: Record<string, string> = {
  TO_THE_ADDRESS: "blue",
  SELF_PICKUP: "green",
  TO_THE_TABLE: "orange",
  EMAIL: "purple",
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AddButton = styled(Button)`
  border-radius: 8px;
  background: ${colors.primary};
  border-color: ${colors.primary};

  &:hover {
    background: ${colors.primaryHover} !important;
    border-color: ${colors.primaryHover} !important;
  }
`;

const tableStyles = {
  borderRadius: 8,
  overflow: 'hidden' as const,
};

const DeliveryZoneCard = styled.div`
  background: var(--color-surface);
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.primary};
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.1);
  }
`;

const ZonesColumn = styled.div`
  max-width: 300px;
`;

const ZoneName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--color-text);
  margin-bottom: 12px;
`;

const ZoneDetail = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
`;

const DeliveryZonesSection = styled.div`
  margin-top: 48px;
`;

const MapContainer = styled.div`
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  overflow: hidden;
  height: 95vh;
  position: sticky;
  top: 16px;
`;

const SimpleModeContainer = styled.div`
  max-width: 600px;
  margin-bottom: 48px;
`;

export const DeliveryMethods: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState<DeliveryMethod | null>(null);
  const [zones, setZones] = useState<any[]>([]);
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<any>(null);
  const [enableFreeDelivery, setEnableFreeDelivery] = useState(false);
  const [form] = Form.useForm();
  const [zoneForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: deliveryMethodsRaw, isLoading } = useQuery(
    ["/delivery-methods", currentBranch?.id],
    () => getDeliveryMethodsForAdmin({ branchId: currentBranch!.id }),
    {
      enabled: !!currentBranch?.id,
    }
  );

  // Sortuj metody po id (data utworzenia) aby kolejność była stała
  const deliveryMethods = React.useMemo(() => {
    if (!deliveryMethodsRaw) return [];
    return [...deliveryMethodsRaw].sort((a, b) => a.id - b.id);
  }, [deliveryMethodsRaw]);

  // Sprawdź czy są metody inne niż podstawowe (TO_THE_ADDRESS, SELF_PICKUP, TO_THE_TABLE)
  const hasAdvancedMethods = React.useMemo(() => {
    return deliveryMethods.some(m =>
      m.type !== 'TO_THE_ADDRESS' && m.type !== 'SELF_PICKUP' && m.type !== 'TO_THE_TABLE'
    );
  }, [deliveryMethods]);

  // Sprawdź czy istnieją aktywne metody podstawowe
  const hasActiveDelivery = React.useMemo(() => {
    return deliveryMethods.some(m => m.type === 'TO_THE_ADDRESS' && m.isActive);
  }, [deliveryMethods]);

  const hasActivePickup = React.useMemo(() => {
    return deliveryMethods.some(m => m.type === 'SELF_PICKUP' && m.isActive);
  }, [deliveryMethods]);

  const hasActiveTableDelivery = React.useMemo(() => {
    return deliveryMethods.some(m => m.type === 'TO_THE_TABLE' && m.isActive);
  }, [deliveryMethods]);

  useQuery(
    ["/delivery-zones", currentBranch?.id],
    () => getDeliveryZones({ id: currentBranch!.id }),
    {
      onSuccess(data) {
        // Sortowanie stref: promienie po wartości radiusKm (rosnąco), wielokąty po id (kolejność tworzenia)
        const sortedZones = [...data].sort((a: any, b: any) => {
          // Jeśli oba mają promień, sortuj po radiusKm
          if (a.radiusKm && b.radiusKm) {
            return a.radiusKm - b.radiusKm;
          }
          // Jeśli tylko a ma promień, a jest pierwsze
          if (a.radiusKm && !b.radiusKm) {
            return -1;
          }
          // Jeśli tylko b ma promień, b jest pierwsze
          if (!a.radiusKm && b.radiusKm) {
            return 1;
          }
          // Jeśli oba są wielokątami, sortuj po id (data utworzenia)
          return a.id - b.id;
        });
        setZones(sortedZones);
      },
      enabled: !!currentBranch?.id,
    }
  );

  const { mutateAsync: createMethod } = useMutation(createDeliveryMethodAdmin, {
    onSuccess: () => {
      notification.success({
        message: t("deliveryMethodsSettings.methodCreatedSuccess"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/delivery-methods"]);
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: (error: any) => {
      notification.error({
        message: t("deliveryMethodsSettings.methodCreateError"),
        description: error?.response?.data?.error || t("deliveryMethodsSettings.genericError"),
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync: updateMethod } = useMutation(updateDeliveryMethodAdmin, {
    onSuccess: () => {
      notification.success({
        message: t("deliveryMethodsSettings.methodUpdatedSuccess"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/delivery-methods"]);
      setIsModalOpen(false);
      setEditingMethod(null);
      form.resetFields();
    },
    onError: (error: any) => {
      notification.error({
        message: t("deliveryMethodsSettings.methodUpdateError"),
        description: error?.response?.data?.error || t("deliveryMethodsSettings.genericError"),
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync: deleteMethod } = useMutation(deleteDeliveryMethodAdmin, {
    onSuccess: () => {
      notification.success({
        message: t("deliveryMethodsSettings.methodDeletedSuccess"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/delivery-methods"]);
    },
    onError: (error: any) => {
      notification.error({
        message: t("deliveryMethodsSettings.methodDeleteError"),
        description: error?.response?.data?.error || t("deliveryMethodsSettings.methodDeleteErrorDescription"),
        placement: "bottomRight",
        duration: 5,
      });
    },
  });

  const { mutateAsync: updateZone } = useMutation(updateDeliveryZone, {
    onSuccess: () => {
      notification.success({
        message: t("deliveryMethodsSettings.zoneUpdatedSuccess"),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries(["/delivery-zones"]);
      setIsZoneModalOpen(false);
      setEditingZone(null);
      zoneForm.resetFields();
      setEnableFreeDelivery(false);
    },
    onError: (error: any) => {
      notification.error({
        message: t("deliveryMethodsSettings.zoneUpdateError"),
        description: error?.response?.data?.error || t("deliveryMethodsSettings.genericError"),
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync: deleteZone, isLoading: isDeleteZoneLoading } = useMutation(
    deleteDeliveryZone,
    {
      onSuccess: async () => {
        notification.success({
          message: t("deliveryMethodsSettings.zoneDeletedSuccess"),
          placement: "bottomRight",
        });
        await queryClient.invalidateQueries(["/delivery-zones"]);
        await queryClient.refetchQueries(["/delivery-zones"]);
        setIsZoneModalOpen(false);
        setEditingZone(null);
        zoneForm.resetFields();
        setEnableFreeDelivery(false);
      },
      onError: (error: any) => {
        notification.error({
          message: t("deliveryMethodsSettings.zoneDeleteError"),
          description: error?.response?.data?.error || t("deliveryMethodsSettings.zoneDeleteErrorDescription"),
          placement: "bottomRight",
        });
      },
    }
  );

  const handleOpenModal = (method?: DeliveryMethod) => {
    if (method) {
      setEditingMethod(method);
      form.setFieldsValue({
        name: method.name,
        type: method.type,
      });
    } else {
      setEditingMethod(null);
      form.resetFields();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const { name, type } = values;

    const body: any = {
      name,
      type,
      companyBranchId: currentBranch?.id,
    };

    if (editingMethod) {
      await updateMethod({ id: editingMethod.id, body });
    } else {
      await createMethod(body);
    }
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: t("deliveryMethodsSettings.confirmDeleteMethodTitle"),
      content: t("deliveryMethodsSettings.confirmDeleteIrreversible"),
      onOk: () => deleteMethod(id),
    });
  };

  const handleOpenZoneModal = (zone: any) => {
    setEditingZone(zone);
    const hasFreeDelivery = zone.freeAbove !== null;
    setEnableFreeDelivery(hasFreeDelivery);
    zoneForm.setFieldsValue({
      name: zone.name,
      price: (zone.price / 100).toFixed(2),
      minimumOrderValue: ((zone.minimumOrderValue || 0) / 100).toFixed(2),
      freeAbove: hasFreeDelivery ? (zone.freeAbove / 100).toFixed(2) : "",
    });
    setIsZoneModalOpen(true);
  };

  const handleZoneSubmit = async () => {
    const values = await zoneForm.validateFields();
    const price = parseFloat(values.price) * 100;
    const freeAbove = enableFreeDelivery && values.freeAbove ? parseFloat(values.freeAbove) * 100 : null;
    const minimumOrderValue = parseFloat(values.minimumOrderValue || "0") * 100;

    await updateZone({
      id: editingZone.id,
      data: {
        name: values.name,
        price,
        freeAbove,
        minimumOrderValue,
      },
    });
  };

  const handleDeleteZone = () => {
    Modal.confirm({
      title: t("deliveryMethodsSettings.confirmDeleteZoneTitle"),
      content: t("deliveryMethodsSettings.confirmDeleteIrreversible"),
      onOk: () => deleteZone({ id: editingZone.id }),
    });
  };

  const handleToggleActive = async (record: DeliveryMethod) => {
    await updateMethod({
      id: record.id,
      body: {
        isActive: !record.isActive,
      },
    });
  };

  const isMethodActive = (record: DeliveryMethod) => {
    return record.isActive;
  };

  // Toggle dla podstawowych metod dostawy (tworzy/usuwa metody)
  const handleToggleBasicMethod = async (type: 'TO_THE_ADDRESS' | 'SELF_PICKUP' | 'TO_THE_TABLE', enabled: boolean) => {
    if (enabled) {
      // Twórz nową metodę
      let name = '';
      if (type === 'TO_THE_ADDRESS') name = t("deliveryMethodsSettings.typeToTheAddress");
      else if (type === 'SELF_PICKUP') name = t("deliveryMethodsSettings.typeSelfPickup");
      else if (type === 'TO_THE_TABLE') name = t("deliveryMethodsSettings.typeToTheTable");

      await createMethod({
        name,
        type,
        companyBranchId: currentBranch?.id,
      });
    } else {
      // Znajdź i usuń metodę
      const method = deliveryMethods.find(m => m.type === type && m.isActive);
      if (method) {
        await deleteMethod(method.id);
      }
    }
  };

  const columns: ColumnsType<DeliveryMethod> = [
    {
      title: t("deliveryMethodsSettings.columnName"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("deliveryMethodsSettings.columnType"),
      dataIndex: "type",
      key: "type",
      render: (type: string) => {
        const color = deliveryTypeColors[type] || "default";
        const labelKey = deliveryTypeLabelKeys[type];
        const label = labelKey ? t(labelKey) : type;
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: t("deliveryMethodsSettings.columnActive"),
      key: "active",
      width: 100,
      render: (_: any, record: DeliveryMethod) => (
        <StyledSwitch
          checked={isMethodActive(record)}
          onChange={() => handleToggleActive(record)}
        />
      ),
    },
    {
      title: t("deliveryMethodsSettings.columnActions"),
      key: "actions",
      render: (_: any, record: DeliveryMethod) => (
        <Space size="middle">
          <Button type="link" size="small" onClick={() => handleOpenModal(record)}>
            {t("deliveryMethodsSettings.edit")}
          </Button>
          <Button type="link" size="small" danger onClick={() => handleDelete(record.id)}>
            {t("deliveryMethodsSettings.delete")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {!hasAdvancedMethods ? (
        // Tryb prosty - tylko switche dla podstawowych metod
        <>
          <SectionTitle style={{ marginBottom: 24 }}>{t("deliveryMethodsSettings.sectionTitle")}</SectionTitle>
          <SimpleModeContainer>
            <SettingItem>
              <SettingRow>
                <StyledSwitch
                  checked={hasActivePickup}
                  onChange={(checked) => handleToggleBasicMethod('SELF_PICKUP', checked)}
                  disabled={isLoading}
                />
                <SettingContent>
                  <SettingLabel>{t("deliveryMethodsSettings.typeSelfPickup")}</SettingLabel>
                  <SettingDescription>
                    {t("deliveryMethodsSettings.selfPickupDescription")}
                  </SettingDescription>
                </SettingContent>
              </SettingRow>
            </SettingItem>

            <SettingItem>
              <SettingRow>
                <StyledSwitch
                  checked={hasActiveTableDelivery}
                  onChange={(checked) => handleToggleBasicMethod('TO_THE_TABLE', checked)}
                  disabled={isLoading}
                />
                <SettingContent>
                  <SettingLabel>{t("deliveryMethodsSettings.typeToTheTable")}</SettingLabel>
                  <SettingDescription>
                    {t("deliveryMethodsSettings.toTheTableDescription")}
                  </SettingDescription>
                </SettingContent>
              </SettingRow>
            </SettingItem>

            <SettingItem>
              <SettingRow>
                <StyledSwitch
                  checked={hasActiveDelivery}
                  onChange={(checked) => handleToggleBasicMethod('TO_THE_ADDRESS', checked)}
                  disabled={isLoading}
                />
                <SettingContent>
                  <SettingLabel>{t("deliveryMethodsSettings.typeToTheAddress")}</SettingLabel>
                  <SettingDescription>
                    {t("deliveryMethodsSettings.toTheAddressDescription")}
                  </SettingDescription>
                </SettingContent>
              </SettingRow>
            </SettingItem>
          </SimpleModeContainer>
        </>
      ) : (
        // Tryb zaawansowany - pełna tabela z możliwością dodawania
        <>
          <HeaderRow>
            <SectionTitle style={{ margin: 0 }}>{t("deliveryMethodsSettings.sectionTitle")}</SectionTitle>
            <AddButton type="primary" onClick={() => handleOpenModal()}>
              {t("deliveryMethodsSettings.addMethod")}
            </AddButton>
          </HeaderRow>

          <Table
            columns={columns}
            dataSource={deliveryMethods}
            loading={isLoading}
            rowKey="id"
            pagination={false}
            style={tableStyles}
          />
        </>
      )}

      {/* Strefy dostawy - pokazuj gdy dostawa pod adres jest włączona */}
      {(hasActiveDelivery || deliveryMethods.some((m) => m.type === "TO_THE_ADDRESS")) && <DeliveryZonesSection>
        <SectionTitle>{t("deliveryMethodsSettings.zonesSectionTitle")}</SectionTitle>
        <Row gutter={24}>
          <Col flex="300px">
            <ZonesColumn>
              {zones.map((zone: any) => (
                <DeliveryZoneCard key={zone.id} onClick={() => handleOpenZoneModal(zone)}>
                  <ZoneName style={{ marginBottom: '12px' }}>{zone.name}</ZoneName>
                  <ZoneDetail style={{ marginBottom: '2px' }}>{t("deliveryMethodsSettings.zonePrice", { value: (zone.price / 100).toFixed(2) })}</ZoneDetail>
                  {zone.freeAbove !== null && (
                    <ZoneDetail style={{ marginBottom: '2px' }}>{t("deliveryMethodsSettings.zoneFreeDeliveryFrom", { value: (zone.freeAbove / 100).toFixed(2) })}</ZoneDetail>
                  )}
                  <ZoneDetail style={{ marginBottom: '2px' }}>
                    {t("deliveryMethodsSettings.zoneMinimumOrderValue", { value: ((zone.minimumOrderValue || 0) / 100).toFixed(2) })}
                  </ZoneDetail>
                  <div style={{ marginTop: '12px' }}>
                    {zone.radiusKm ? (
                      <Tag color="blue">{t("deliveryMethodsSettings.zoneRadius", { value: zone.radiusKm })}</Tag>
                    ) : (
                      <Tag color="green">{t("deliveryMethodsSettings.zonePolygon")}</Tag>
                    )}
                  </div>
                </DeliveryZoneCard>
              ))}
            </ZonesColumn>
          </Col>
          <Col flex="auto">
            <MapContainer>
              <DeliveryMap zones={zones} branchId={currentBranch?.id} />
            </MapContainer>
          </Col>
        </Row>
      </DeliveryZonesSection>}

      {/* Modal dodawania/edycji metod - tylko w trybie zaawansowanym */}
      {hasAdvancedMethods && (
        <Modal
          title={editingMethod ? t("deliveryMethodsSettings.editMethodTitle") : t("deliveryMethodsSettings.addMethodTitle")}
          open={isModalOpen}
          onOk={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingMethod(null);
            form.resetFields();
          }}
          width={600}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
            <Form.Item
              name="name"
              label={t("deliveryMethodsSettings.columnName")}
              rules={[{ required: true, message: t("deliveryMethodsSettings.nameRequired") }]}
            >
              <Input placeholder={t("deliveryMethodsSettings.namePlaceholder")} />
            </Form.Item>

            <Form.Item
              name="type"
              label={t("deliveryMethodsSettings.columnType")}
              rules={[{ required: true, message: t("deliveryMethodsSettings.typeRequired") }]}
            >
              <Select placeholder={t("deliveryMethodsSettings.typePlaceholder")}>
                <Select.Option value="TO_THE_ADDRESS">{t("deliveryMethodsSettings.typeToTheAddress")}</Select.Option>
                <Select.Option value="SELF_PICKUP">{t("deliveryMethodsSettings.typeSelfPickup")}</Select.Option>
                <Select.Option value="TO_THE_TABLE">{t("deliveryMethodsSettings.typeToTheTable")}</Select.Option>
                <Select.Option value="EMAIL">{t("deliveryMethodsSettings.typeEmail")}</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}

      <Modal
        title={t("deliveryMethodsSettings.editZoneTitle")}
        open={isZoneModalOpen}
        onOk={handleZoneSubmit}
        onCancel={() => {
          setIsZoneModalOpen(false);
          setEditingZone(null);
          zoneForm.resetFields();
          setEnableFreeDelivery(false);
        }}
        footer={[
          <Button
            key="delete"
            danger
            onClick={handleDeleteZone}
            loading={isDeleteZoneLoading}
            style={{ float: "left" }}
          >
            {t("deliveryMethodsSettings.delete")}
          </Button>,
          <Button
            key="cancel"
            onClick={() => {
              setIsZoneModalOpen(false);
              setEditingZone(null);
              zoneForm.resetFields();
              setEnableFreeDelivery(false);
            }}
          >
            {t("deliveryMethodsSettings.cancel")}
          </Button>,
          <Button key="submit" type="primary" onClick={handleZoneSubmit}>
            {t("deliveryMethodsSettings.save")}
          </Button>,
        ]}
      >
        <Form form={zoneForm} layout="vertical">
          <Form.Item
            name="name"
            label={t("deliveryMethodsSettings.zoneNameLabel")}
            rules={[{ required: true, message: t("deliveryMethodsSettings.fieldRequired") }]}
          >
            <Input
              style={{
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
            />
          </Form.Item>

          <Form.Item
            name="price"
            label={t("deliveryMethodsSettings.zonePriceLabel")}
            rules={[{ required: true, message: t("deliveryMethodsSettings.fieldRequired") }]}
          >
            <Input
              type="number"
              style={{
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
              pattern="^\d+(\.\d{1,2})?$"
            />
          </Form.Item>

          <Form.Item
            name="minimumOrderValue"
            label={t("deliveryMethodsSettings.minimumOrderValueLabel")}
            rules={[{ required: true, message: t("deliveryMethodsSettings.fieldRequired") }]}
          >
            <Input
              type="number"
              style={{
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
              pattern="^\d+(\.\d{1,2})?$"
            />
          </Form.Item>

          <Form.Item
            label={
              <Checkbox
                checked={enableFreeDelivery}
                onChange={(e) => {
                  setEnableFreeDelivery(e.target.checked);
                  if (!e.target.checked) {
                    zoneForm.setFieldsValue({ freeAbove: undefined });
                  }
                }}
              >
                {t("deliveryMethodsSettings.freeDeliveryCheckboxLabel")}
              </Checkbox>
            }
            name="freeAbove"
            rules={[{ required: enableFreeDelivery, message: t("deliveryMethodsSettings.fieldRequired") }]}
          >
            <Input
              type="number"
              disabled={!enableFreeDelivery}
              style={{
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
              pattern="^\d+(\.\d{1,2})?$"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
